import { addAlertAction } from 'app/store/Alerts/slice';
import axios, { AxiosResponse } from 'axios';
import {
  getCreateReleaseLink,
  getCreateReleaseVersionLink,
  getCurrentlyReplacingAudio,
  getDeleteReleaseLink,
  getDeleteReleaseSongLink,
  getFetchAudioLink,
  getListReleasesLink,
  getListReleaseVersionsLink,
  getListVersionSongAudioLink,
  getMoveReleaseVersionLink,
  getPatchReleaseExportLink,
  getReleaseExportLink,
  getReleaseLink,
  getReleaseSongTitleUpdateLink,
  getReleaseVersion,
  getReleaseVersionDeleteLink,
  getReleaseVersionLink,
  getReleaseVersionMetadataUpdateLink,
  getReleaseVersionSyncLink,
  getReplacePrimaryAudioLink,
  getSoundCloudPlaylistTypesDDLLink,
  getUpdateReleaseLink,
  getUpdateReleaseVersionAudioLink,
  getUpdateReleaseVersionLink,
  getUpdateSoundCloudReleaseExportLink,
  getUpdateVersionSongLink,
  getVersionCreditsUpdateLink,
  getVersionTypeDDLink,
} from 'app/store/Releases/selectors';
import {
  AttachAudioToReleaseResponse,
  AttachAudioVersionsToReleaseData,
  CreateReleaseSongRequestData,
  ExportReleaseData,
  FetchCreateReleaseValidationsResponse,
  FetchExportReleaseValidationsResponse,
  FetchReleaseVersionResponse,
  FetchUpdateReleaseSongTitleValidationsResponse,
  FetchUpdateReleaseValidationsResponse,
  FetchUpdateReleaseVersionValidationsResponse,
  FetchUpdateSCVersionAudioValidationsResponse,
  FetchUpdateVersionValidationsResponse,
  GetReleasesListResponse,
  LabelV2Release,
  ListReleaseVersionsResponse,
  PlaylistTypesResponse,
  ReleaseExportConnexResponse,
  ReleaseExportResponse,
  ReleaseResponse,
  ReleaseType,
  ReleaseVersionReorderBody,
  ReleaseVersionTypesDDl,
  UpdateReleaseVersionContributionResponse,
  UpdateReleaseVersionMetadataResponse,
  UpdateReleaseVersionSongPatchData,
  UpdateVersionSongResponse,
} from 'types/releases';
import { SoundCloudVersionDetailsFormData } from 'app/components/Releases/SoundCloudVersionDetailsForm/types';
import { format } from 'date-fns';
import { Link } from 'types/api.types';
import {
  buildQueryUrl,
  dateToTime,
  emptyStringToNull,
  isConflictError,
  poll,
} from 'app/utils/utils';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { dateFormats } from 'app/constants';
import { createAsyncThunk } from 'app/store/utils';
import {
  MultiTrackExportRequestData,
  SingleTrackExportRequestData,
} from 'app/components/Releases/ExportSoundCloudReleaseDialog/types';
import { addToDownloadList } from 'app/store/Shares/slice';
import { FetchAudioResponse, FetchSongAudioListResponse } from 'types/audio';
import { SelectedAudio, ValidationContext } from 'app/components/Audio';
import { convertTCFormDataToVersionPatchData } from 'app/components/Releases/TuneCoreVersionDetailsForm/utils';
import { convertSCFormDataToVersionPatchData } from 'app/components/Releases/SoundCloudVersionDetailsForm/utils';
import { TuneCoreVersionDetailsFormData } from 'app/components/Releases/TuneCoreVersionDetailsForm/types';
import { ExportTuneCoreReleaseFormValues } from 'app/components/Releases/ExportTuneCoreReleaseDialog/types';
import { AccountConnectionProviderId } from 'types/profile';
import { LabelV2VersionDetailsFormData } from 'app/components/Releases/LabelV2VersionDetails/types';
import { ExportConnexReleaseFormValues } from 'app/components/Releases/ExportSendLabelV2Release';
import {
  CreateReleaseVersionEffectData,
  SyncVersionEffectData,
  UpdateVersionCreditsEffectData,
} from './effects.types';
import { getUpdateVersionCreditsMessage } from './utils';
import { AlertIcon } from '../Alerts/slice.types';
import { getConnection } from '../Profile/selectors';

const sortByTitle = {
  sortBy: 'title',
};

const RESERVED_ISRC_PREFIX = 'SE5IE';

export const createReleaseEffect = createAsyncThunk(
  'releases/create',
  async (
    data: { songs?: CreateReleaseSongRequestData[]; type?: ReleaseType },
    { getState, dispatch }
  ) => {
    const link = getCreateReleaseLink(getState());
    try {
      if (!link) {
        throw new Error('Create Release link not found');
      }
      const url = buildQueryUrl(link?.href, sortByTitle);
      const response = await axios.request<ReleaseResponse>({
        url,
        method: link?.method,
        data: {
          title: `Release ${format(new Date(), dateFormats.LIST_FORMAT)}`,
          songs: data.songs,
          type: data.type || ReleaseType.LABEL,
        },
        withCredentials: true,
      });

      if (data.type !== ReleaseType.SOUNDCLOUD) {
        dispatch(addAlertAction({ message: 'Release created' }));
      }
      return response.data.response.release.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to create Release, please try again',
        })
      );
      throw e;
    }
  }
);

export const attachAudioToReleaseEffect = createAsyncThunk(
  'releases/attachAudio',
  async (
    data: {
      selectedAudioArray: SelectedAudio[];
      releaseGuid: string;
      link?: Link;
    },
    { dispatch }
  ) => {
    const { link, releaseGuid, selectedAudioArray } = data;
    const releaseVersionsData: AttachAudioVersionsToReleaseData[] = selectedAudioArray.map(
      (audio) => ({
        audioGuid: audio.audioGuid,
        songGuid: audio.songGuid,
        type: audio.type,
      })
    );
    try {
      if (!link) {
        throw new Error('Attach Audio link not found');
      }
      const response = await axios.request<AttachAudioToReleaseResponse>({
        url: link.href,
        method: 'POST',
        data: {
          releaseGuid,
          versions: releaseVersionsData,
        },
        withCredentials: true,
      });

      return response.data.response.versions;
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode ===
        ApiErrorCodeTypes.MAX_RELEASE_VERSION_COUNT_IN_TUNECORE_RELEASE_EXCEEDED
      ) {
        dispatch(
          addAlertAction({
            message:
              ApiErrorCodeMessages.MAX_RELEASE_VERSION_COUNT_IN_TUNECORE_RELEASE_EXCEEDED,
          })
        );
      } else {
        dispatch(
          addAlertAction({
            message: 'Failed to add audio, please try again',
          })
        );
      }

      throw e;
    }
  }
);

export const fetchCreateReleaseValidationsEffect = createAsyncThunk(
  'releases/fetchCreateValidations',
  async (_, { getState }) => {
    const link = getCreateReleaseLink(getState());
    if (!link) {
      throw new Error('Create Release link not found');
    }
    const { data } = await axios.request<FetchCreateReleaseValidationsResponse>(
      {
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );
    return data.response.schema.properties.body.properties;
  }
);

export const fetchReleaseVersionEffect = createAsyncThunk(
  'releases/fetchVersion',
  async (
    data: {
      songGuid?: string;
      versionGuid: string;
      releaseGuid?: string;
      href?: string;
      validation?: boolean;
    },
    { getState, dispatch }
  ) => {
    const link = data.href ?? getReleaseVersionLink(getState());

    try {
      if (!link) {
        throw new Error('Get Release version link not found');
      }
      const response = await axios.get<FetchReleaseVersionResponse>(
        link
          .replace('{songGuid}', data?.songGuid || '')
          .replace('{releaseGuid}', data?.releaseGuid || '')
          .replace('{versionGuid}', data.versionGuid),
        {
          withCredentials: true,
        }
      );
      return response.data.response.releaseVersion.item;
    } catch (e: any) {
      if (
        e.response?.data?.error?.statusReason ===
        ApiErrorCodeTypes.ENTITY_NOT_FOUND
      ) {
        dispatch(addAlertAction({ message: 'Song Version not found' }));
      }
      throw e;
    }
  }
);

export const fetchReleaseEffect = createAsyncThunk(
  'releases/fetchRelease',
  async (data: { guid: string; silent?: boolean }, { getState, dispatch }) => {
    const link = getReleaseLink(getState());

    try {
      if (!link) {
        throw new Error('Get Release link not found');
      }
      const url = buildQueryUrl(
        link?.href.replace('{releaseGuid}', data.guid),
        sortByTitle
      );

      const response = await axios.request<ReleaseResponse>({
        url,
        method: link?.method,
        withCredentials: true,
      });

      const result = response.data.response.release.item;
      if (result.type === ReleaseType.LABEL_V2) {
        (result as LabelV2Release).versions.forEach(
          ({ guid, song: { guid: songGuid } }) => {
            dispatch(
              fetchReleaseVersionEffect({
                versionGuid: guid,
                songGuid,
                releaseGuid: result.guid,
                href: result._links?.getReleaseVersion?.href,
                validation: true,
              })
            );
          }
        );
      }

      return response.data.response.release.item;
    } catch (e) {
      if (!data.silent) {
        dispatch(
          addAlertAction({ message: 'Failed to get Release, please try again' })
        );
      }

      throw e;
    }
  }
);

export const getReleasesListEffect = createAsyncThunk(
  'releases/fetchReleases',
  async (data: { next?: Link }, { getState }) => {
    const link = getListReleasesLink(getState());
    if (!link) {
      throw new Error('Releases list link not found');
    }
    const response = await axios.get<GetReleasesListResponse>(
      data?.next?.href || buildQueryUrl(link.href, {}),
      {
        withCredentials: true,
      }
    );

    const { items, _links } = response.data.response.release;
    return { items, _links };
  }
);

export const updateReleaseSongTitleEffect = createAsyncThunk(
  'releases/updateSongTitle',
  async (data: { title: string; songGuid: string }, { getState, dispatch }) => {
    const link = getReleaseSongTitleUpdateLink(getState());
    try {
      if (!link) {
        throw new Error('Get Release song title update link not found');
      }
      const url = buildQueryUrl(
        link?.href.replace('{songGuid}', data.songGuid),
        sortByTitle
      );
      const response = await axios.request<ReleaseResponse>({
        url,
        method: link?.method,
        data: { title: data.title },
        withCredentials: true,
      });

      dispatch(addAlertAction({ message: 'Title updated' }));
      return response.data.response.release.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to update title, please try again' })
      );
      throw e;
    }
  }
);

export const fetchUpdateReleaseSongTitleValidationsEffect = createAsyncThunk(
  'releases/fetchUpdateReleaseSongTitleValidations',
  async (data: { songGuid: string }, { getState }) => {
    const link = getReleaseSongTitleUpdateLink(getState());
    if (!link) {
      throw new Error('Get Release song title update link not found');
    }
    const response = await axios.request<FetchUpdateReleaseSongTitleValidationsResponse>(
      {
        url: link?.href.replace('{songGuid}', data.songGuid),
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );

    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchUpdateVersionSongValidationsEffect = createAsyncThunk(
  'releases/fetchUpdateVersionValidations',
  async (_, { getState }) => {
    const link = getUpdateVersionSongLink(getState());
    if (!link) {
      throw new Error('Update Version link not found');
    }
    const response = await axios.request<FetchUpdateVersionValidationsResponse>(
      {
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );

    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchUpdateReleaseVersionValidationsEffect = createAsyncThunk(
  'releases/fetchUpdateReleaseVersionValidations',
  async (_, { getState }) => {
    const link = getUpdateReleaseVersionLink(getState());
    if (!link) {
      throw new Error('Update Release Version link not found');
    }
    const response = await axios.request<FetchUpdateReleaseVersionValidationsResponse>(
      {
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );

    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchUpdateSCVersionAudioValidationsEffect = createAsyncThunk(
  'releases/fetchUpdateSCVersionAudioValidations',
  async (
    data: {
      audioGuid: string;
    },
    { getState }
  ) => {
    const link = getUpdateReleaseVersionAudioLink(getState());

    if (!link) {
      throw new Error('Update Release Version Audio link not found');
    }
    const response = await axios.request<FetchUpdateSCVersionAudioValidationsResponse>(
      {
        url: link?.href.replace('{audioGuid}', data.audioGuid),
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );
    return response.data.response.schema.properties.body.properties;
  }
);

export const updateSCVersionAudioDetailsEffect = createAsyncThunk(
  'releases/updateSCVersionAudioDetails',
  async (
    data: {
      audioGuid: string;
      isrc: string;
    },
    { getState, dispatch }
  ) => {
    const link = getUpdateReleaseVersionAudioLink(getState());
    try {
      if (!link) {
        throw new Error('Update Release Version Audio link not found');
      }
      await axios.request({
        url: link?.href.replace('{audioGuid}', data.audioGuid),
        method: link?.method,
        data: { isrc: emptyStringToNull(data.isrc) },
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'ISRC has been updated' }));
    } catch (e: any) {
      if (
        e.response?.data?.error?.statusCode === 400 &&
        data.isrc.startsWith(RESERVED_ISRC_PREFIX)
      ) {
        dispatch(
          addAlertAction({
            message: `ISRCs starting ${RESERVED_ISRC_PREFIX} are allocated by Session Studio, and cannot be entered manually.`,
          })
        );
      } else if (isConflictError(e)) {
        dispatch(addAlertAction({ message: 'ISRC is already set' }));
      } else {
        dispatch(
          addAlertAction({
            message: 'Failed to update ISRC, please try again',
          })
        );
      }
      throw e;
    }
  }
);

export const updateReleaseVersionEffect = createAsyncThunk(
  'releases/updateReleaseVersion',
  async (
    data: {
      releaseType:
        | ReleaseType.TUNECORE
        | ReleaseType.SOUNDCLOUD
        | ReleaseType.LABEL_V2;
      formData:
        | Partial<SoundCloudVersionDetailsFormData>
        | Partial<TuneCoreVersionDetailsFormData>
        | Partial<LabelV2VersionDetailsFormData>;
    },
    { getState, dispatch }
  ) => {
    const link = getUpdateReleaseVersionLink(getState());
    try {
      if (!link) {
        throw new Error('Update Release Version link not found');
      }
      await axios.request({
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        data:
          data.releaseType === ReleaseType.SOUNDCLOUD
            ? convertSCFormDataToVersionPatchData(data.formData)
            : convertTCFormDataToVersionPatchData(data.formData),
      });
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to save details, please try again' })
      );
      throw e;
    }
  }
);

export const updateLabelVersionEffect = createAsyncThunk(
  'releases/updateLabelVersion',
  async (
    data: { formData: UpdateReleaseVersionSongPatchData },
    { getState, dispatch }
  ) => {
    const link = getUpdateReleaseVersionLink(getState());
    const releaseVersion = getReleaseVersion(getState());
    try {
      if (!link) {
        throw new Error('Update Version link not found');
      }

      await axios.request<UpdateVersionSongResponse>({
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        data: {
          song: data.formData,
          versionTitle: data.formData?.versionTitle,
        },
      });
      return {
        updated: dateToTime(new Date()),
        versionGuid: releaseVersion?.metadata.guid,
        songGuid: releaseVersion?.song.songGuid,
      };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to save details, please try again' })
      );
      throw e;
    }
  }
);

export const fetchUpdateReleaseValidationsEffect = createAsyncThunk(
  'releases/fetchUpdateReleaseValidations',
  async (_, { getState }) => {
    const link = getUpdateReleaseLink(getState());
    if (!link) {
      throw new Error('Get Release details link not found');
    }

    const response = await axios.request<FetchUpdateReleaseValidationsResponse>(
      {
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );

    return response.data.response.schema.properties.body.properties;
  }
);

export const updateReleaseEffect = createAsyncThunk(
  'releases/update',
  async (data: { title: string }, { getState, dispatch }) => {
    const link = getUpdateReleaseLink(getState());
    try {
      if (!link) {
        throw new Error('Get Release details link not found');
      }

      const url = buildQueryUrl(link?.href, sortByTitle);
      const response = await axios.request<ReleaseResponse>({
        url,
        method: link?.method,
        data,
        withCredentials: true,
      });
      return response.data.response.release.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to update title, please try again' })
      );
      throw e;
    }
  }
);

export const exportReleaseEffect = createAsyncThunk(
  'releases/export',
  async (
    {
      data,
      download,
      releaseGuid,
    }: { data: ExportReleaseData; releaseGuid?: string; download?: boolean },
    { getState, dispatch, rejectWithValue }
  ) => {
    const link = getReleaseExportLink(getState());
    try {
      if (!link) {
        throw new Error('Export Release link not found');
      }

      await axios.request({
        url: link?.href,
        method: link?.method,
        data: download ? { downloadExport: data } : { shipExport: data },
        withCredentials: true,
      });

      if (!download) {
        dispatch(addAlertAction({ message: 'Release sent' }));
      } else if (releaseGuid) {
        dispatch(addToDownloadList(releaseGuid));
        dispatch(
          addAlertAction({
            id: releaseGuid,
            message: 'Preparing export',
            statusIcon: AlertIcon.LOADING,
            autoHideDuration: 15000,
          })
        );
      }
      return true;
    } catch (e: any) {
      if (
        e.response?.data?.error?.errorCode !==
        ApiErrorCodeTypes.INVALID_PROJECT_ID_PROVIDED
      ) {
        dispatch(
          addAlertAction({
            message: 'Failed to send Release, please try again',
          })
        );
      }
      return rejectWithValue(e);
    }
  }
);

export const fetchExportReleaseValidationsEffect = createAsyncThunk(
  'releases/fetchExportReleaseValidations',
  async (_, { getState }) => {
    const link = getReleaseExportLink(getState());
    if (!link) {
      throw new Error('Export Release link not found');
    }

    const response = await axios.request<FetchExportReleaseValidationsResponse>(
      {
        url: link?.href,
        method: link?.method,
        withCredentials: true,
        headers: { Accept: 'application/json+schema' },
      }
    );
    return response.data.response.schema.properties.body.properties;
  }
);

export const fetchVersionTypesDDLEffect = createAsyncThunk(
  'releases/fetchVersionTypeDDL',
  async (_, { getState }) => {
    const link = getVersionTypeDDLink(getState());
    if (!link) {
      throw new Error('Version type DDL Link Not Found');
    }
    const response = await axios.get<ReleaseVersionTypesDDl>(link.href);
    return response.data.types;
  }
);

export const syncVersionEffect = createAsyncThunk(
  'releases/syncVersion',
  async (data: SyncVersionEffectData, { getState, dispatch }) => {
    const state = getState();
    const syncLink = data.link || getReleaseVersionSyncLink(state);
    try {
      if (!syncLink) {
        throw new Error('Sync release version link not found');
      }
      await axios.request({
        url: syncLink.href,
        method: syncLink.method,
        withCredentials: true,
      });

      const link = getReleaseVersionLink(state);
      const currentRelease = getReleaseVersion(state);
      if (!link || !currentRelease) {
        throw new Error(
          'Get Release version link or current release not found'
        );
      }

      const getReleaseVersionRequest = () =>
        axios.get<FetchReleaseVersionResponse>(
          link
            .replace('{songGuid}', data.songGuid)
            .replace('{versionGuid}', data.versionGuid),
          {
            withCredentials: true,
          }
        );

      const songDetailsCondition = (
        result: AxiosResponse<FetchReleaseVersionResponse>
      ) =>
        currentRelease.metadata.songSynced.timestamp <
        result.data.response.releaseVersion.item.metadata.songSynced.timestamp;

      const songCreditsCondition = (
        result: AxiosResponse<FetchReleaseVersionResponse>
      ) =>
        currentRelease.metadata.contributionsSynced.timestamp <
        result.data.response.releaseVersion.item.metadata.contributionsSynced
          .timestamp;

      const songLyricsCondition = (
        result: AxiosResponse<FetchReleaseVersionResponse>
      ) => {
        if (!result.data.response.releaseVersion.item.metadata.lyricsSynced) {
          return false;
        }
        return (
          (currentRelease.metadata.lyricsSynced?.timestamp || 0) <
          result.data.response.releaseVersion.item.metadata.lyricsSynced
            .timestamp
        );
      };

      const songSplitsCondition = (
        result: AxiosResponse<FetchReleaseVersionResponse>
      ) => {
        if (!result.data.response.releaseVersion.item.metadata.splitSynced) {
          return false;
        }

        return (
          (currentRelease.metadata.splitSynced?.timestamp || 0) <
          result.data.response.releaseVersion.item.metadata.splitSynced
            .timestamp
        );
      };

      const syncCondition = (
        result: AxiosResponse<FetchReleaseVersionResponse>
      ) =>
        songDetailsCondition(result) &&
        songCreditsCondition(result) &&
        (data.releaseType === ReleaseType.SOUNDCLOUD ||
          (songLyricsCondition(result) && songSplitsCondition(result)));

      const response = await poll<FetchReleaseVersionResponse>(
        getReleaseVersionRequest,
        syncCondition,
        3000,
        5
      );
      dispatch(
        addAlertAction({ message: 'Release information synced and updated' })
      );

      dispatch(
        fetchReleaseEffect({
          guid: currentRelease.metadata.releaseGuid,
          silent: true,
        })
      );

      return response.data.response.releaseVersion.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to sync Song data, please try again',
        })
      );
      throw e;
    }
  }
);

export const createReleaseVersionEffect = createAsyncThunk(
  'releases/createReleaseVersion',
  async (data: CreateReleaseVersionEffectData, { getState, dispatch }) => {
    const link = getCreateReleaseVersionLink(getState());
    try {
      if (!link) {
        throw new Error('Add Song Version link not found');
      }
      const url = buildQueryUrl(link?.href, sortByTitle);
      const response = await axios.request<ReleaseResponse>({
        url,
        method: link?.method,
        data: {
          songs: data.songs,
        },
        withCredentials: true,
      });
      if (data.context === 'single') {
        dispatch(addAlertAction({ message: 'Version added' }));
      } else if (data.context === 'multiple') {
        dispatch(
          addAlertAction({
            message: data.songs.length === 1 ? 'Song added' : 'Songs added',
          })
        );
      }
      return response.data.response.release.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to add Version, please try again' })
      );
      throw e;
    }
  }
);

export const updateVersionCreditsEffect = createAsyncThunk(
  'releases/updateVersionCredits',
  async (data: UpdateVersionCreditsEffectData, { getState, dispatch }) => {
    const link = getVersionCreditsUpdateLink(getState());
    const shouldDispatchMessage =
      data.showSnackBar && data.body.isExcluded !== undefined;
    try {
      if (!link) {
        throw new Error('update version Credits link not found');
      }
      const response = await axios.request<UpdateReleaseVersionContributionResponse>(
        {
          url: link.href.replace('{contributionGuid}', data.contributionGuid),
          method: link.method,
          withCredentials: true,
          data: data.body,
        }
      );
      if (shouldDispatchMessage) {
        dispatch(
          addAlertAction({
            message: getUpdateVersionCreditsMessage({
              isExcluded: Boolean(data.body.isExcluded),
              success: true,
            }),
          })
        );
      }
      return response.data.response.releaseVersionContribution.item;
    } catch (e) {
      if (shouldDispatchMessage) {
        dispatch(
          addAlertAction({
            message: getUpdateVersionCreditsMessage({
              isExcluded: Boolean(data.body.isExcluded),
              success: false,
            }),
          })
        );
      }
      throw e;
    }
  }
);

export const updateReleaseVersionMetadataEffect = createAsyncThunk(
  'releases/updateReleaseVersionMetadata',
  async (
    data: { versionGuid: string; type: string },
    { getState, dispatch }
  ) => {
    const link = getReleaseVersionMetadataUpdateLink(getState());
    try {
      if (!link) {
        throw new Error('Get update Release Version details link not found');
      }

      const response = await axios.request<UpdateReleaseVersionMetadataResponse>(
        {
          url: link?.href.replace('{versionGuid}', data.versionGuid),
          method: link?.method,
          data: {
            type: data.type,
          },
          withCredentials: true,
        }
      );
      dispatch(addAlertAction({ message: 'Version updated' }));
      return response.data.response.releaseVersionMetadata.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to update Version, please try again',
        })
      );
      throw e;
    }
  }
);

export const deleteReleaseVersionEffect = createAsyncThunk(
  'releases/deleteReleaseVersion',
  async (data: { versionGuid: string }, { getState, dispatch }) => {
    const link = getReleaseVersionDeleteLink(getState());
    try {
      if (!link) {
        throw new Error('Get Release Version delete link not found');
      }

      const response = await axios.request<ReleaseResponse>({
        url: link?.href.replace('{versionGuid}', data.versionGuid),
        method: link?.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Version deleted' }));
      return response.data.response.release.item;
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to delete Version, please try again',
        })
      );
      throw e;
    }
  }
);

export const deleteReleaseSongEffect = createAsyncThunk(
  'releases/deleteReleaseSong',
  async (data: { songGuid: string }, { getState, dispatch }) => {
    const { songGuid } = data;
    try {
      const link = getDeleteReleaseSongLink(getState());
      if (!link) {
        throw new Error('Delete Release song link not found');
      }
      const response = await axios.request<ReleaseResponse>({
        url: link?.href.replace('{songGuid}', songGuid),
        method: link?.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Release song deleted' }));
      return response.data.response.release.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to delete song, please try again' })
      );
      throw e;
    }
  }
);

export const deleteReleaseEffect = createAsyncThunk(
  'releases/deleteRelease',
  async (data: { releaseGuid: string }, { getState, dispatch }) => {
    try {
      const link = getDeleteReleaseLink(getState());
      if (!link) {
        throw new Error('Delete Release link not found');
      }
      await axios.request({
        url: link?.href.replace('{releaseGuid}', data.releaseGuid),
        method: link?.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Release deleted' }));
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to delete Release, please try again',
        })
      );
      throw e;
    }
  }
);

export const moveReleaseVersionEffect = createAsyncThunk(
  'releases/moveReleaseVersion',
  async (data: ReleaseVersionReorderBody, { getState, dispatch }) => {
    const link = getMoveReleaseVersionLink(getState());

    try {
      if (!link) {
        throw new Error('Move Release version link not found');
      }

      await axios.post<Response>(link, data, {
        withCredentials: true,
      });

      dispatch(addAlertAction({ message: 'Release Version reordered' }));
    } catch (e: any) {
      if (
        e.response?.data?.error?.statusReason === ApiErrorCodeTypes.CONFLICT
      ) {
        dispatch(addAlertAction({ message: 'Release Version not found' }));
      }
      throw e;
    }
  }
);

export const fetchReleaseVersionSongAudioListEffect = createAsyncThunk(
  'release/fetchVersionAudioList',
  async (data: { validationContext?: ValidationContext }, { getState }) => {
    const { validationContext } = data;
    const audioListLink = getListVersionSongAudioLink(getState());
    if (!audioListLink) {
      throw new Error('Fetch Song Audio List link not found');
    }
    const url = buildQueryUrl(audioListLink.href, {
      ...(validationContext ? { validationContext } : {}),
    });
    const response = await axios.get<FetchSongAudioListResponse>(url, {
      withCredentials: true,
    });
    const { items } = response.data.response.audio;

    return items;
  }
);

export const replaceVersionAudioEffect = createAsyncThunk(
  'release/replaceVersionAudio',
  async (data: { audioGuid: string }, { getState, dispatch }) => {
    const state = getState();
    const replaceAudioLink = getReplacePrimaryAudioLink(state);
    const releaseVersion = getReleaseVersion(state);
    try {
      if (!replaceAudioLink) {
        throw new Error('Replace Version Audio List link not found');
      }
      await axios.request({
        method: replaceAudioLink.method,
        url: replaceAudioLink.href,
        withCredentials: true,
        data: {
          audioGuid: data.audioGuid,
          ...(releaseVersion?.metadata.primaryAudioGuid
            ? {
                currentPrimaryAudioGuid:
                  releaseVersion?.metadata.primaryAudioGuid,
              }
            : {}),
        },
      });
    } catch (e) {
      if (isConflictError(e)) {
        dispatch(
          addAlertAction({
            message: 'Audio version mismatch. Synced successfully',
          })
        );
        throw e;
      }
    }
  }
);

export const fetchAudioEffect = createAsyncThunk(
  'release/fetchAudio',
  async (data: { guid: string; parentGuid: string }, { getState }) => {
    const fetchAudioLink = getFetchAudioLink(getState());
    if (!fetchAudioLink) {
      throw new Error('Fetch Song Audio link not found');
    }
    const response = await axios.get<FetchAudioResponse>(
      fetchAudioLink.href
        .replace('{audioGuid}', data.guid)
        .replace('{songGuid}', data.parentGuid),
      {
        withCredentials: true,
      }
    );
    return response.data.response.audio.item;
  }
);

export const handleReplaceAudioResetEffect = createAsyncThunk(
  'release/handleReplaceAudioReset',
  async (data: { versionGuid: string }, { getState, dispatch }) => {
    const currentlyReplacingAudio = getCurrentlyReplacingAudio(getState());
    if (currentlyReplacingAudio[data.versionGuid]) {
      dispatch(addAlertAction({ message: 'Failed to update audio version' }));
      return true;
    }
    throw new Error('Audio is already replaced');
  }
);

export const exportScReleaseEffect = createAsyncThunk(
  'releases/exportScRelease',
  async (
    data: {
      data: SingleTrackExportRequestData | MultiTrackExportRequestData;
      releaseGuid: string;
      audioCount?: number;
    },
    { getState, dispatch, rejectWithValue }
  ) => {
    const link = getReleaseExportLink(getState());

    try {
      if (!link) {
        throw new Error('Export Release link not found');
      }

      const result = await axios.request<ReleaseExportResponse>({
        url: link?.href,
        method: link?.method,
        data: data.data,
        withCredentials: true,
      });
      if (!data?.data?.isDraft) {
        dispatch(
          addAlertAction({
            id: data.releaseGuid,
            message: 'Preparing SoundCloud release export',
            statusIcon: AlertIcon.LOADING,
            autoHideDuration: 15000,
          })
        );
      } else {
        dispatch(
          addAlertAction({
            message: `Your draft saved! Please connect to SoundCloud to finish export`,
            autoHideDuration: 5000,
          })
        );
      }
      return result.data.response.releaseExport.item;
    } catch (err: any) {
      dispatch(
        addAlertAction({
          message: `Failed to export ${
            'playlist' in data.data ? 'playlist' : 'track'
          } to SoundCloud, please try again`,
        })
      );
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const exportTcReleaseEffect = createAsyncThunk(
  'releases/exportTcRelease',
  async (
    data: {
      data: ExportTuneCoreReleaseFormValues;
      accountConnectionGuid?: string;
      releaseGuid: string;
    },
    { getState, dispatch }
  ) => {
    const link = getReleaseExportLink(getState());

    try {
      if (!link) {
        throw new Error('Export Release link not found');
      }

      const isDraft = !data.accountConnectionGuid;
      const response = await axios.request<ReleaseExportResponse>({
        url: link?.href,
        method: link?.method,
        data: {
          accountConnectionGuid: data.accountConnectionGuid,
          mainArtist: data.data.mainArtist,
          releaseDate: format(data.data.releaseDate as Date, 'yyyy-MM-dd'),
          genre: data.data.genre?.value,
          language: data.data.language?.value,
          recordingLocation: data.data.recordingLocation,
          isDraft,
        },
        withCredentials: true,
      });
      if (isDraft) {
        dispatch(
          addAlertAction({
            message: `Your draft saved! Please connect to TuneCore to finish export`,
            autoHideDuration: 5000,
          })
        );
      } else {
        dispatch(
          addAlertAction({
            id: data.releaseGuid,
            message: 'Preparing TuneCore release export',
            statusIcon: AlertIcon.LOADING,
            autoHideDuration: 15000,
          })
        );
      }

      return response.data.response.releaseExport.item;
    } catch (e: any) {
      let message = 'Failed to export to TuneCore, please try again';
      if (e?.response?.data?.error?.statusCode === 404) {
        message =
          'TuneCore connection not found, please connect to TuneCore and try again';
      }
      dispatch(addAlertAction({ message }));
      throw e;
    }
  }
);

export const exportConnexReleaseEffect = createAsyncThunk(
  'releases/exportConnexRelease',
  async (
    {
      data: { shipmentStrategy },
      accountConnectionGuid,
      releaseGuid,
    }: {
      data: ExportConnexReleaseFormValues;
      accountConnectionGuid?: string;
      releaseGuid: string;
    },
    { getState, dispatch }
  ) => {
    const link = getReleaseExportLink(getState());

    try {
      if (!link) {
        throw new Error('Export Release link not found');
      }

      const message =
        shipmentStrategy === 'Connex'
          ? 'Preparing Connex release export'
          : 'Preparing Label Copy download';
      dispatch(
        addAlertAction({
          id: releaseGuid,
          message,
          statusIcon: AlertIcon.LOADING,
          autoHideDuration: 180000,
        })
      );

      const response = await axios.request<ReleaseExportConnexResponse>({
        url: link?.href,
        method: link?.method,
        data: {
          accountConnectionGuid,
          shipmentStrategy,
        },
        withCredentials: true,
      });

      if (accountConnectionGuid) {
        const connection = getConnection(accountConnectionGuid)(getState());

        if (connection?._links.disconnect?.href) {
          await axios.request({
            url: connection._links.disconnect.href,
            method: connection._links.disconnect.method,
            withCredentials: true,
          });
        }
      } else {
        dispatch(addToDownloadList(releaseGuid));
      }

      return response.data.response.releaseExport.item;
    } catch (e: any) {
      let message = 'Failed to export to Connex, please try again';
      if (e?.response?.data?.error?.statusCode === 404) {
        message =
          'Connex recipient not found. Please validate email \u00A0\u00A0 address and try again';
      }
      dispatch(addAlertAction({ message, id: releaseGuid }));
      throw e;
    }
  }
);

export const fetchSoundCloudPlaylistTypesDDLEffect = createAsyncThunk(
  'releases/fetchSoundCloudPlaylistTypesDDL',
  async (_, { getState }) => {
    const link = getSoundCloudPlaylistTypesDDLLink(getState());

    if (!link) {
      throw new Error('SoundCloud playlist types list link not found');
    }

    const response = await axios.request<PlaylistTypesResponse>({
      url: link.href,
      method: link.method,
    });
    return response.data;
  }
);

export const patchReleaseExportEffect = createAsyncThunk(
  'releases/patchReleaseExport',
  async (
    data: {
      data: { accountConnectionGuid: string };
      releaseExportGuid: string;
      providerId: AccountConnectionProviderId;
    },
    { getState, dispatch }
  ) => {
    const link = getPatchReleaseExportLink(getState());

    if (!link) {
      throw new Error('Release export draft patching link not found');
    }

    try {
      await axios.request({
        url: link.href.replace('{releaseExportGuid}', data.releaseExportGuid),
        method: link.method,
        data: data.data,
        withCredentials: true,
      });
    } catch (error) {
      dispatch(
        addAlertAction({
          message: `Failed to export to ${data.providerId}, please try again`,
        })
      );
      throw error;
    }
  }
);

export const updateReleaseExportEffect = createAsyncThunk(
  'releases/updateReleaseExport',
  async (
    data: {
      releaseExportGuid: string;
      releaseGuid: string;
      providerId: AccountConnectionProviderId;
    },
    { getState, dispatch }
  ) => {
    const link = getUpdateSoundCloudReleaseExportLink(getState());

    if (!link) {
      throw new Error('Release export draft update link not found');
    }

    try {
      await axios.request({
        url: link.href.replace('{releaseExportGuid}', data.releaseExportGuid),
        method: link.method,
        withCredentials: true,
      });
      dispatch(
        addAlertAction({
          id: data.releaseGuid,
          message: `Preparing ${data.providerId} release export`,
          statusIcon: AlertIcon.LOADING,
          autoHideDuration: 15000,
        })
      );
    } catch (error) {
      dispatch(
        addAlertAction({
          message: `Failed to export to ${data.providerId}, please try again`,
        })
      );
    }
  }
);

export const fetchReleaseVersionsDetailsEffect = createAsyncThunk(
  'releases/fetchReleaseVersionsDetails',
  async (_, { getState }) => {
    const link = getListReleaseVersionsLink(getState());

    if (!link) {
      throw new Error('List release versions link not found');
    }

    const response = await axios.request<ListReleaseVersionsResponse>({
      url: link.href,
      method: link.method,
      withCredentials: true,
    });

    return response.data?.response?.releaseVersion?.items;
  }
);
